<template>
  <div class="logout-container">
    <div class="logout-card">
      <!-- Logo/Header -->
      <div class="logout-header">
        <div class="logo-container">
          <img src="@/assets/images/logo-dark.png" alt="Logo" class="logout-logo" />
        </div>
        <h4 class="logout-title">{{ logoutTitle }}</h4>
        <p class="logout-message">{{ logoutMessage }}</p>
      </div>

      <!-- Simple Loading Indicator -->
      <div v-if="!logoutComplete && !error" class="logout-loader">
        <div class="spinner-container">
          <div class="logout-spinner"></div>
        </div>
      </div>

      <!-- Success Icon -->
      <div v-if="logoutComplete && !error" class="logout-success">
        <div class="success-icon">
          <i class="ri-check-line"></i>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="alert alert-warning mt-3">
        <i class="ri-error-warning-line mr-2"></i>
        {{ error }}
        <br>
        <small>Redirigiendo al login...</small>
      </div>

      <!-- Manual Action (only if logout is complete and no auto-redirect) -->
      <div v-if="logoutComplete && !hasLogoutRedirectUrl" class="logout-actions">
        <button 
          class="btn btn-primary"
          @click="goToLogin"
        >
          <i class="ri-login-circle-line mr-1"></i>
          Ir al Login
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { authMethods, authComputed } from "@/state/helpers";
import ssoService from "@/services/sso.service";

export default {
  name: "Logout",
  
  data() {
    return {
      // Logout redirect URL (for SSO cascade logout)
      logoutRedirectUrl: null,
      
      // UI state
      error: null,
      logoutComplete: false,
      
      // Messages
      logoutTitle: "Cerrando sesión...",
      logoutMessage: "Por favor espera un momento."
    };
  },
  
  computed: {
    ...authComputed,
    
    // Check if we have a logout redirect URL
    hasLogoutRedirectUrl() {
      return !!this.logoutRedirectUrl;
    }
  },
  
  async created() {
    await this.performLogout();
  },
  
  methods: {
    ...authMethods,
    
    /**
     * Perform complete logout flow
     */
    async performLogout() {
      try {
        // Detect referer_url from SSO params
        const ssoParams = ssoService.detectSSOParams();
        const refererUrl = ssoParams?.referer_url || null;
        
        // Backend logout
        const logoutData = await this.logout({ refererUrl });
        
        if (!logoutData || !logoutData.success) {
          throw new Error('Logout failed');
        }
        
        // Extract logout redirect URL
        this.logoutRedirectUrl = logoutData.logout_redirect_url || null;
        
        // Redirect to cascade logout URL if provided
        if (this.logoutRedirectUrl) {
          this.logoutTitle = "Sesión cerrada";
          this.logoutMessage = "Redirigiendo...";
          
          // Wait a moment to show the message
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Perform redirect to cascade logout
          window.location.href = this.logoutRedirectUrl;
        } else {
          // No cascade logout needed, go to login
          this.completeLogout();
        }
        
      } catch (error) {
        console.error('Logout error:', error);
        this.error = 'Hubo un problema al cerrar sesión. La sesión local ha sido cerrada correctamente.';
        
        // Still redirect to login after error
        setTimeout(() => {
          this.goToLogin();
        }, 3000);
      }
    },
    
    /**
     * Complete logout process
     */
    completeLogout() {
      this.logoutComplete = true;
      this.logoutTitle = "Sesión cerrada";
      this.logoutMessage = "¡Hasta pronto!";
      
      // Clean SSO params from URL
      ssoService.cleanSSOParamsFromURL();
      
      // Auto-redirect to login after 2 seconds
      setTimeout(() => {
        this.goToLogin();
      }, 2000);
    },
    
    /**
     * Navigate to login page
     */
    goToLogin() {
      this.$router.push('/login');
    }
  }
};
</script>

<!-- Styles are imported from src/assets/scss/custom/pages/_logout.scss -->


